/**
 * Where a layout container keeps the controls placed inside it.
 *
 * The player (FormField) reads these same keys, so the designer must write to them:
 *  - card-container, collapsible-panel: config.controls
 *  - enhanced-tabs: config.tabs[i].controls
 *  - stepper: config.steps[i].controls
 *
 * A drop on a tabs or stepper container goes into its first tab or step. When the list is missing
 * or empty, it is created from the same default labels the player shows, so the dropped control
 * is never hidden behind a list that has no entries.
 */

import type { FormControl } from '@atlas-forms/types-js';

type ListKey = 'tabs' | 'steps';

interface ContentLayout {
  /** Config key that holds the controls directly */
  direct?: true;
  /** Config key of a tab or step list; each entry holds its own controls */
  list?: ListKey;
  defaultLabels?: string[];
}

const CONTENT_LAYOUTS: Record<string, ContentLayout> = {
  'card-container': { direct: true },
  'collapsible-panel': { direct: true },
  'enhanced-tabs': { list: 'tabs', defaultLabels: ['Tab 1', 'Tab 2', 'Tab 3'] },
  stepper: { list: 'steps', defaultLabels: ['Step 1', 'Step 2', 'Step 3'] },
};

type ListEntry = { label?: string; controls?: FormControl[] };

/** True for a layout container that can hold controls */
export function isContentContainer(type: string | undefined): boolean {
  return !!type && type in CONTENT_LAYOUTS;
}

/** Controls held directly by a container (its first tab or step, for list layouts) */
export function getContainerControls(control: FormControl): FormControl[] {
  const layout = CONTENT_LAYOUTS[control.type];
  if (!layout) return [];
  const config = control.config ?? {};
  if (layout.direct) return (config.controls as FormControl[] | undefined) ?? [];
  const entries = (config[layout.list!] as ListEntry[] | undefined) ?? [];
  return entries[0]?.controls ?? [];
}

/**
 * The config for `control` with `child` appended to its content. `child` is stored with the
 * next order value. Returns null when the control is not a content container.
 */
export function configWithAppendedChild(
  control: FormControl,
  child: FormControl
): Record<string, unknown> | null {
  const layout = CONTENT_LAYOUTS[control.type];
  if (!layout) return null;
  const config = (control.config ?? {}) as Record<string, unknown>;

  if (layout.direct) {
    const existing = (config.controls as FormControl[] | undefined) ?? [];
    return { ...config, controls: [...existing, { ...child, order: existing.length }] };
  }

  const key = layout.list!;
  const stored = config[key] as ListEntry[] | undefined;
  const entries: ListEntry[] = stored && stored.length > 0
    ? stored
    : (layout.defaultLabels ?? []).map((label) => ({ label }));
  const first = entries[0];
  const existing = first.controls ?? [];
  const updated: ListEntry[] = [
    { ...first, controls: [...existing, { ...child, order: existing.length }] },
    ...entries.slice(1),
  ];
  return { ...config, [key]: updated };
}
